/**
 * The shared presentation of a report screen (UI phase D, ADR 0031): the context line (period, site and
 * filter chips; on paper also the brand header), the summary cards with the comparison to the previous
 * equal period, the coverage note and the empty state. Every figure comes from the report's own rows.
 */
import type { ReactNode } from "react";
import "../../styles/report-present.css";
import { BarChart } from "../../components/charts/BarChart";
import { EmptyState, Panel } from "../../components/ui";
import { useI18n, type TFn } from "../../i18n";
import type { ChartSpec } from "../../lib/chart";
import { formatDateInput } from "../../lib/date-input";
import { formatDateTimeIn } from "../../lib/export/sheet";
import { formatNum, formatPct, periodDays, previousPeriod, shiftDay, type Coverage, type Trend } from "../../lib/report-figures";
import { navigate } from "../../router/hash";
import { filtersQuery, todayLocal, type ReportFilters } from "./shared";

/** "27.10.2026 – 02.11.2026 (7 gün)" — the period as one string (screen chips and PDF). */
export function periodText(t: TFn, lang: "tr" | "en", from: string, to: string): string {
  const days = periodDays(from, to);
  return t(days === 1 ? "reports.periodOne" : "reports.periodMany", { from: formatDateInput(from, lang), to: formatDateInput(to, lang), days });
}

/** The comparison line of a summary figure ("Önceki döneme göre %12,3 artış"); null = none. */
export function trendText(t: TFn, locale: string, trend: Trend | null | undefined): string | null {
  if (!trend) return null;
  if (trend.kind === "flat") return t("reports.trend.flat");
  if (trend.kind === "new") return t("reports.trend.new");
  const delta = trend.unit === "pts" ? t("reports.trend.points", { value: formatNum(trend.delta, locale, 1) }) : formatPct(trend.delta, locale, 1);
  return t(trend.kind === "up" ? "reports.trend.up" : "reports.trend.down", { delta });
}

export type SummaryItem = {
  label: string;
  value: string;
  /** Previous equal period (same RPC, same filters): the change and the previous value. */
  trend?: Trend | null;
  previous?: string | null;
};

/**
 * Title-adjacent context: the period, the site and the other filters as chips, the time zone and the
 * sample-data marker. Printing adds the brand header (logo, product) above the title.
 */
export function ReportContext({ chips, from, to, tz, sample, period }: { chips: [string, string][]; from?: string; to?: string; tz: string | null; sample: boolean; period?: string }) {
  const { t, lang } = useI18n();
  const p = period ?? (from && to ? periodText(t, lang, from, to) : null);
  return (
    <div className="report-context">
      <ul className="report-chips" aria-label={t("reports.context")}>
        {p && (
          <li className="report-chip report-chip-period">
            <span className="report-chip-key">{t("reports.period")}</span> <span className="report-chip-value">{p}</span>
          </li>
        )}
        {chips.map(([k, v]) => (
          <li key={k} className="report-chip">
            <span className="report-chip-key">{k}</span> <span className="report-chip-value">{v}</span>
          </li>
        ))}
      </ul>
      <p className="report-meta">
        {tz && <span>{t("reports.tz", { tz })}</span>}
        {sample && <span className="badge tone-warn">{t("reports.sample")}</span>}
      </p>
    </div>
  );
}

/** Brand header, shown only on paper (print stylesheet): logo, product name, when it was printed. */
export function PrintBrand({ title, tz }: { title: string; tz: string | null }) {
  const { t, lang } = useI18n();
  return (
    <div className="print-brand" aria-hidden="true">
      <img src={`${import.meta.env.BASE_URL}brand/expartise-logo.png`} alt="" width={150} height={48} />
      <div>
        <div className="print-brand-product">{t("app.productLabel")}</div>
        <div className="print-brand-title">{title}</div>
        <div className="print-brand-at">
          {t("reports.export.generatedAt")}: {formatDateTimeIn(new Date().toISOString(), tz ?? "Europe/Istanbul", lang)}
        </div>
      </div>
    </div>
  );
}

/** 3–5 key figures; the trend line only when the previous period could be compared. */
export function SummaryCards({ items, busy, prevFrom, prevTo }: { items: SummaryItem[]; busy?: boolean; prevFrom?: string; prevTo?: string }) {
  const { t, lang, locale } = useI18n();
  const prevPeriod = prevFrom && prevTo ? `${formatDateInput(prevFrom, lang)} – ${formatDateInput(prevTo, lang)}` : null;
  return (
    <dl className="report-summary" aria-label={t("reports.summary")} aria-busy={busy || undefined}>
      {items.map((it) => {
        const tr = trendText(t, locale, it.trend);
        const dir = it.trend && (it.trend.kind === "up" || it.trend.kind === "down") ? it.trend.kind : "flat";
        return (
          <div key={it.label} className="report-summary-item">
            <dt>{it.label}</dt>
            <dd className="report-summary-value">{it.value}</dd>
            {tr && (
              <dd className={`report-summary-trend trend-${dir}`} title={prevPeriod && it.previous ? t("reports.trend.previous", { period: prevPeriod, value: it.previous }) : undefined}>
                <span aria-hidden="true" className="trend-mark">
                  {dir === "up" ? "▲" : dir === "down" ? "▼" : "•"}
                </span>{" "}
                {tr}
                {prevPeriod && it.previous && <span className="sr-only"> ({t("reports.trend.previous", { period: prevPeriod, value: it.previous })})</span>}
              </dd>
            )}
          </div>
        );
      })}
    </dl>
  );
}

/** The primary chart in a panel, with an optional note under it. */
export function ChartPanel({ title, spec, note, controls }: { title: string; spec: ChartSpec | null; note?: ReactNode; controls?: ReactNode }) {
  if (!spec || spec.bars.length === 0) return null;
  return (
    <div className="report-chart">
      <Panel title={title} meta={controls}>
        <BarChart spec={spec} label={title} />
        {note && <p className="chart-note">{note}</p>}
      </Panel>
    </div>
  );
}

/** What the console knows about missing data in the period (days not yet reached, devices gone quiet). */
export function CoverageNote({ coverage, tz }: { coverage: Coverage; tz: string }) {
  const { t, lang } = useI18n();
  if (coverage.futureDays === 0 && coverage.lastDataAt.length === 0 && coverage.silentAll.length === 0) return null;
  return (
    <div className="coverage-note" role="note">
      <strong>{t("reports.coverage.title")}</strong>
      <ul>
        {coverage.futureDays > 0 && <li>{t("reports.coverage.future")}</li>}
        {coverage.lastDataAt.length > 0 && (
          <li>
            {t("reports.coverage.since")}{" "}
            {coverage.lastDataAt.map((s, i) => (
              <span key={s.fleetNo}>
                {i > 0 && ", "}
                <span className="mono">{s.fleetNo}</span> ({formatDateTimeIn(s.at, tz, lang)})
              </span>
            ))}
          </li>
        )}
        {coverage.silentAll.length > 0 && <li>{t("reports.coverage.all", { vehicles: coverage.silentAll.join(", ") })}</li>}
      </ul>
      {(coverage.lastDataAt.length > 0 || coverage.silentAll.length > 0) && <p className="muted">{t("reports.coverage.limit")}</p>}
    </div>
  );
}

/**
 * No rows for the period: says which period and site, and offers the last 30 days (within every report's
 * range limit) when the user is looking at another period.
 */
export function ReportEmpty({ path, filters, siteName, keep }: { path: string; filters: ReportFilters; siteName: string; keep?: Record<string, string | undefined> }) {
  const { t, lang } = useI18n();
  const today = todayLocal();
  const last30 = { from: shiftDay(today, -29), to: today };
  const isLast30 = filters.from === last30.from && filters.to === last30.to;
  return (
    <EmptyState
      compact
      icon="search"
      title={t("reports.empty.title")}
      action={
        isLast30 ? undefined : (
          <button type="button" className="button button-secondary" onClick={() => navigate(path, { ...filtersQuery({ ...filters, ...last30 }), ...keep })}>
            {t("reports.empty.last30")}
          </button>
        )
      }
    >
      {t("reports.empty.body", { period: periodText(t, lang, filters.from, filters.to), site: siteName })}
    </EmptyState>
  );
}

/** The previous equal period of a report's filters (for the comparison). */
export function previousOf(f: Pick<ReportFilters, "from" | "to">) {
  return previousPeriod(f.from, f.to);
}
